import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import apiClient from '@app/config/axios.config'
import type { MapLayer } from '@app/types/layerTypes'
import { POLL_INTERVAL_MS } from '../Live.config'
import { useTransformLiveLayers } from '../hooks/useTransformLiveLayers'
import { BASE_API_URL } from '@app/app-constants'
import { LiveContextInterface, LiveLayer, SelectedLiveFeature } from '../Live.types'

const LiveContext = createContext<LiveContextInterface>({
  liveLayers: [],
  setLiveLayers: () => {},
  toggleLiveFeedVisibility: () => {},
  toggleLiveFeedInteractivity: () => {},
  activeLiveLayers: [],
  setActiveLiveLayers: () => {},
  liveData: {},
  refreshFeed: async () => {},
  riskLayers: [],
  riskLayerVisibility: {},
  toggleRiskLayer: () => {},
  liveMapSelected: false,
  setLiveMapSelected: () => {},
  selectedLiveFeatures: [],
  setSelectedLiveFeatures: () => {},
})

export function LiveContextProvider({ children }: { children: React.ReactNode }) {
  const { transformLiveFeedToGeoJSON } = useTransformLiveLayers()

  const [liveLayers, setLiveLayers] = useState<LiveLayer[]>([])
  const [activeLiveLayers, setActiveLiveLayers] = useState<LiveLayer[]>([])
  const [liveData, setLiveData] = useState<Record<string, GeoJSON.FeatureCollection | null>>({})
  const [liveLayersLoading, setLiveLayersLoading] = useState<boolean>(false)
  const [riskLayers, setRiskLayers] = useState<MapLayer[]>([])
  const [riskLayerVisibility, setRiskLayerVisibility] = useState<Record<string, boolean>>({})
  const [liveMapSelected, setLiveMapSelected] = useState<boolean>(false)
  const [selectedLiveFeatures, setSelectedLiveFeatures] = useState<SelectedLiveFeature[]>([])

  useEffect(() => {
    setLiveData((prev) => Object.fromEntries(liveLayers.map((l) => [l.id, prev[l.id] ?? null])))
  }, [liveLayers])

  useEffect(() => {
    apiClient
      .get<MapLayer[]>('/layer')
      .then((res) => {
        const layers = Array.isArray(res.data) ? res.data : []
        setRiskLayers(layers)
        setRiskLayerVisibility(Object.fromEntries(layers.map((l) => [l.id, false])))
      })
      .catch(() => {})
  }, [])

  const fetchFeed = useCallback(
    async (layer: LiveLayer) => {
      if (!layer.url) return

      try {
        let url = layer.url
        if (layer.proxy_url)
          url = url.replace(layer.proxy_url, `${BASE_API_URL}/layer/live_feeds/${layer.id}/proxy/`)
        const response = await fetch(url, { credentials: layer.proxy_url ? 'include' : undefined })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await transformLiveFeedToGeoJSON(layer, response)

        let lastUpdated: Date | string = 'None'
        const cfg = layer.last_updated_config

        if (cfg?.source === 'alertProperties') {
          if (data.lastUpdated) lastUpdated = new Date(data.lastUpdated)
        }
        // imageHeader is resolved per-feature in SpecificLocationAccordion

        setLiveLayers((prev) =>
          prev.map((l) => (l.id === layer.id ? { ...l, lastUpdated, isError: false } : l)),
        )
        setLiveData((prev) => ({ ...prev, [layer.id]: data }))
      } catch {
        // on first load, set empty data so the loading state clears; subsequent poll failures keep the last successful data
        setLiveData((prev) => ({
          ...prev,
          [layer.id]: prev[layer.id] ?? { type: 'FeatureCollection', features: [] },
        }))
        setLiveLayers((prev) => prev.map((l) => (l.id === layer.id ? { ...l, isError: true } : l)))
      }
    },
    [transformLiveFeedToGeoJSON],
  )

  const activeLiveLayersStructureKey = activeLiveLayers.map((layer) => layer.id).join(',')

  useEffect(() => {
    if (activeLiveLayers.length === 0) {
      setLiveLayersLoading(false)
      return
    }

    let cancelled = false
    setLiveLayersLoading(true)

    // Track when all initial fetches resolve, then clear loading.
    // The poll interval re-fetches silently without resetting the loading state.
    const fetchPromises = activeLiveLayers.map((layer) => fetchFeed(layer))
    Promise.all(fetchPromises).then(() => {
      if (!cancelled) setLiveLayersLoading(false)
    })

    const interval = setInterval(() => {
      activeLiveLayers.forEach((layer) => fetchFeed(layer))
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
    // Only restart the poll when the set of active layer IDs changes.
    // Property-only changes (interactivityDisabled, visible) don't affect data fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLiveLayersStructureKey, fetchFeed])

  const refreshFeed = useCallback(
    async (id: string) => {
      const layer = activeLiveLayers.find((l) => l.id === id) ?? liveLayers.find((l) => l.id === id)
      if (layer) await fetchFeed(layer)
    },
    [fetchFeed, activeLiveLayers, liveLayers],
  )

  const toggleLiveFeedVisibility = useCallback((id: string) => {
    setActiveLiveLayers((prev) =>
      prev.map((liveFeed) =>
        liveFeed.id === id ? { ...liveFeed, visible: !liveFeed.visible } : liveFeed,
      ),
    )
  }, [])

  const toggleRiskLayer = useCallback((id: string) => {
    setRiskLayerVisibility((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const toggleLiveFeedInteractivity = useCallback((id: string) => {
    setActiveLiveLayers((prev) =>
      prev.map((liveFeed) =>
        liveFeed.id === id
          ? { ...liveFeed, interactivityDisabled: !liveFeed.interactivityDisabled }
          : liveFeed,
      ),
    )
  }, [])

  return (
    <LiveContext.Provider
      value={{
        liveLayers,
        setLiveLayers,
        toggleLiveFeedVisibility,
        toggleLiveFeedInteractivity,
        activeLiveLayers,
        setActiveLiveLayers,
        liveData,
        refreshFeed,
        riskLayers,
        riskLayerVisibility,
        toggleRiskLayer,
        liveMapSelected,
        setLiveMapSelected,
        selectedLiveFeatures,
        setSelectedLiveFeatures,
      }}
    >
      {children}
    </LiveContext.Provider>
  )
}

export const useLiveContext = () => useContext(LiveContext)
